import { createClient } from "@supabase/supabase-js";

export const SUPA_URL    = import.meta.env.VITE_SUPA_URL    || "https://fjpmovyrvuuvgdhwusec.supabase.co";
export const SUPA_KEY    = import.meta.env.VITE_SUPA_KEY    || "sb_publishable_9LcA-mtVfk7nKRj-02Ie-Q_3YQv7suY";
export const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "https://bloom-backend-v55a.onrender.com";

// Supabase JS client — used for Realtime subscriptions only
export const supabaseClient = createClient(SUPA_URL, SUPA_KEY);

export const SH = {
  "apikey":        SUPA_KEY,
  "Authorization": `Bearer ${SUPA_KEY}`,
  "Content-Type":  "application/json",
  "Prefer":        "return=representation",
};

export const supa = {
  async select(table, q="") {
    try {
      const r = await fetch(`${SUPA_URL}/rest/v1/${table}?${q}`, { headers: SH });
      if (!r.ok) return [];
      return await r.json();
    } catch { return []; }
  },
  // Like select(), but a failure is NOT disguised as "no rows": retries with backoff and returns null if it
  // still fails, so callers can tell "network problem" apart from "genuinely empty / not found".
  async selectRetry(table, q="", tries=4) {
    for (let i = 0; i < tries; i++) {
      try {
        const r = await fetch(`${SUPA_URL}/rest/v1/${table}?${q}`, { headers: SH });
        if (r.ok) { const rows = await r.json(); if (Array.isArray(rows)) return rows; }
        else if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429) return null; // permanent
      } catch { /* network — retry */ }
      if (i < tries - 1) await new Promise(res => setTimeout(res, 700 * (i + 1)));
    }
    return null;
  },
  // Pages are retried individually. Previously ONE failed page silently ended the loop, dropping every row
  // after it (and the list is oldest-first, so the NEWEST products went missing on slow connections).
  async selectAll(table, q="") {
    const PAGE = 1000;
    let offset = 0;
    const all = [];
    while (true) {
      let rows = null;
      for (let i = 0; i < 4 && rows === null; i++) {
        try {
          const r = await fetch(`${SUPA_URL}/rest/v1/${table}?${q}&limit=${PAGE}&offset=${offset}`, { headers: SH });
          if (r.ok) { const j = await r.json(); if (Array.isArray(j)) rows = j; }
          else if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429) break; // permanent
        } catch { /* network — retry */ }
        if (rows === null && i < 3) await new Promise(res => setTimeout(res, 700 * (i + 1)));
      }
      if (!rows || rows.length === 0) break;
      all.push(...rows);
      if (rows.length < PAGE) break;
      offset += PAGE;
    }
    return all;
  },
  async insert(table, data) {
    try {
      const r = await fetch(`${SUPA_URL}/rest/v1/${table}`, {
        method:"POST", headers:SH, body:JSON.stringify(data),
      });
      if (!r.ok) { console.error("Supa insert:", await r.text()); return null; }
      const text = await r.text();
      if (!text) return data;
      const rows = JSON.parse(text);
      return Array.isArray(rows) ? (rows[0] || data) : rows;
    } catch(e) { console.error(e); return null; }
  },
  async upsert(table, data) {
    try {
      const r = await fetch(`${SUPA_URL}/rest/v1/${table}`, {
        method:"POST",
        headers:{ ...SH, "Prefer":"resolution=merge-duplicates,return=minimal" },
        body:JSON.stringify(data),
      });
      if (!r.ok) { console.error("Supa upsert:", await r.text()); return null; }
      return data;
    } catch(e) { console.error(e); return null; }
  },
  async patch(table, filter, data) {
    try {
      const r = await fetch(`${SUPA_URL}/rest/v1/${table}?${filter}`, {
        method:"PATCH", headers:SH, body:JSON.stringify(data),
      });
      if (!r.ok) { console.error("Supa patch:", await r.text()); return null; }
      return await r.json();
    } catch(e) { console.error(e); return null; }
  },
  async del(table, filter) {
    try {
      const r = await fetch(`${SUPA_URL}/rest/v1/${table}?${filter}`, {
        method:"DELETE", headers:SH,
      });
      return r.ok;
    } catch { return false; }
  },
  async update(table, filter, data) {
    try {
      const r = await fetch(`${SUPA_URL}/rest/v1/${table}?${filter}`, {
        method:"PATCH", headers:{...SH,"Prefer":"return=minimal"}, body:JSON.stringify(data),
      });
      return r.ok;
    } catch { return false; }
  },
};
